import { z } from 'zod';

export const webSearchTimeRangeSchema = z.enum(['day', 'month', 'year']);
// Staan is the only web search provider (decision 2026-10-06): no other engine can be
// selected, and no automatic fallback to another engine exists anymore.
export const webSearchProviderSchema = z.enum(['staan']);
// Read-only tolerance: responses and usage records produced before 2026-10-06 (or by a
// backend-web not yet redeployed) may still carry `searxng` and a `fallbackReason`.
// Never use these schemas to select a provider.
export const legacyWebSearchProviderSchema = z.enum(['searxng', 'staan']);
/** @deprecated Legacy SearXNG fallback reason, tolerated on read only. */
export const webSearchFallbackReasonSchema = z.enum([
  'provider_config',
  'language',
  'budget',
  'rate_limit',
  'circuit_open',
  'staan_error',
]);
// Why Staan could not serve a search (backend-web `WEB_SEARCH_PROVIDER_UNAVAILABLE`).
// Evaluation precedence: config > language > budget > rate_limit > circuit_open > provider_error.
export const webSearchUnavailableReasonSchema = z.enum([
  'config',
  'language',
  'budget',
  'rate_limit',
  'circuit_open',
  'provider_error',
]);
const webHttpUrlSchema = z.url({
  error: 'L URL web est invalide.',
  protocol: /^https?$/,
});
export const webResearchFreshnessSchema = z.enum(['any', 'day', 'month', 'year']);
export const webResearchModeSchema = z.enum(['off', 'standard', 'deep']);
export const webResearchReasonSchema = z.enum([
  'deep_research_requested',
  'explicit_web_request',
  'high_stakes',
  'internal_corpus_request',
  'recommendation',
  'stable_knowledge',
  'temporal_information',
  'url_provided',
  'verification_requested',
  'web_continuity',
  'web_toggle_default',
  'writing_only',
]);
export const webSourceKindSchema = z.enum(['official', 'primary', 'secondary', 'unknown']);
export const webResearchTargetSourceKindSchema = webSourceKindSchema.exclude(['unknown']);
export const chatRouteIntentConfidenceSchema = z.enum(['high', 'medium', 'low']);

export const chatRouteActionSchema = z.enum([
  'answer',
  'clarifyIntent',
  'createDocument',
  'createRichDocument',
  // Modify the last document generated in the conversation (only offered when one exists).
  'reviseDocument',
]);

export const chatRouteDecisionSchema = z.object({
  action: chatRouteActionSchema,
  deliverableExplicit: z.boolean(),
  deliverableMentioned: z.boolean(),
  intentConfidence: chatRouteIntentConfidenceSchema,
  metaConversational: z.boolean(),
  research: z.object({
    freshness: webResearchFreshnessSchema,
    mode: webResearchModeSchema,
    reasons: z.array(webResearchReasonSchema).max(10),
  }).strict(),
  title: z.string().trim().min(1).max(160).nullable(),
  topic: z.string().trim().min(1).max(500),
}).strict().refine((decision) => (
  !['clarifyIntent', 'createDocument', 'createRichDocument', 'reviseDocument'].includes(decision.action)
  || decision.research.mode === 'off'
), {
  error: 'La creation de document et la clarification d intention ne peuvent pas activer la recherche web.',
  path: ['research', 'mode'],
}).refine((decision) => (
  decision.action !== 'clarifyIntent' || decision.title === null
), {
  error: 'La clarification d intention ne doit pas definir de titre de document.',
  path: ['title'],
});

export const webResearchPlanSchema = z.object({
  facets: z.array(z.string().trim().min(1).max(160)).max(5),
  freshness: webResearchFreshnessSchema,
  queries: z.array(z.string().trim().min(1).max(500)).min(1).max(4),
  targetSourceTypes: z.array(webResearchTargetSourceKindSchema).max(3),
}).strict();

const webResearchActivityStatusSchema = z.enum(['completed', 'running']);
export const webResearchSynthesisStageSchema = z.enum(['drafting', 'reviewing']);

export const webResearchActivitySchema = z.discriminatedUnion('phase', [
  z.object({
    phase: z.literal('planning'),
    status: webResearchActivityStatusSchema,
  }).strict(),
  z.object({
    phase: z.literal('searching'),
    queryCount: z.number().int().nonnegative(),
    status: webResearchActivityStatusSchema,
  }).strict(),
  z.object({
    // Host name (without `www.`) of the page being read, for the live status line.
    // Optional: absent on messages persisted before 2026-10-06.
    currentSite: z.string().trim().min(1).max(253).optional(),
    pageCount: z.number().int().nonnegative(),
    phase: z.literal('reading'),
    status: webResearchActivityStatusSchema,
  }).strict(),
  z.object({
    // Research time, from the first web activity to the last search/read.
    // Optional: absent on messages persisted before 2026-10-06.
    durationMs: z.number().int().nonnegative().optional(),
    phase: z.literal('synthesizing'),
    // Live waiting state of a running synthesis: the draft is being written,
    // then checked against the sources before display. Additive and optional:
    // absent on completed phases, older messages and clients ignore it.
    stage: webResearchSynthesisStageSchema.optional(),
    status: webResearchActivityStatusSchema,
  }).strict(),
]);

export const webSearchRequestSchema = z.object({
  language: z.string().trim().min(2).max(10).default('fr'),
  maxResults: z.number().int().min(1).max(20).default(8),
  provider: webSearchProviderSchema.optional(),
  query: z.string().trim().min(1, {
    error: 'La requete de recherche web est requise.',
  }).max(500),
  timeRange: webSearchTimeRangeSchema.optional(),
});

const nullableSearchMetadataSchema = z.preprocess(
  (value) => value === null ? undefined : value,
  z.string().trim().min(1).optional(),
);

export const webSearchResultSchema = z.object({
  domain: z.string().trim().min(1),
  engine: nullableSearchMetadataSchema,
  publishedAt: nullableSearchMetadataSchema,
  rank: z.number().int().positive(),
  snippet: z.string(),
  title: z.string(),
  url: webHttpUrlSchema,
});

export const webSearchResponseSchema = z.object({
  cached: z.boolean(),
  effectiveProvider: legacyWebSearchProviderSchema.optional(),
  /** @deprecated Never emitted since 2026-10-06; tolerated on read only. */
  fallbackReason: webSearchFallbackReasonSchema.optional(),
  freshnessFiltered: z.boolean().optional(),
  timeRangeRelaxed: z.boolean().optional(),
  latencyMs: z.number().int().nonnegative(),
  query: z.string(),
  queryTruncated: z.boolean().optional(),
  requestedTimeRange: webSearchTimeRangeSchema.optional(),
  results: z.array(webSearchResultSchema),
  selectedProvider: legacyWebSearchProviderSchema.optional(),
  staanRequestCount: z.number().int().nonnegative().optional(),
});

// Error envelope of backend-web (`{ error: { code, message, ... } }`). `reason` and
// `staanRequestCount` are only set for WEB_SEARCH_PROVIDER_UNAVAILABLE.
export const webToolsErrorResponseSchema = z.object({
  error: z.object({
    code: z.string().trim().min(1),
    message: z.string(),
    reason: webSearchUnavailableReasonSchema.optional(),
    staanRequestCount: z.number().int().nonnegative().optional(),
  }),
});

export const webFetchModeSchema = z.enum(['auto', 'http', 'dynamic', 'stealth']);
export const webFetchResolvedModeSchema = z.enum(['http', 'dynamic', 'stealth']);

export const webFetchRequestSchema = z.object({
  maxChars: z.number().int().min(500).max(50_000).default(12_000),
  mode: webFetchModeSchema.default('auto'),
  url: webHttpUrlSchema.max(2048),
});

export const webFetchResponseSchema = z.object({
  author: z.string().nullable().optional(),
  cached: z.boolean(),
  contentType: z.string().nullable(),
  fetchMode: webFetchResolvedModeSchema,
  finalUrl: webHttpUrlSchema,
  latencyMs: z.number().int().nonnegative(),
  publishedAt: z.string().trim().min(1).nullable().optional(),
  text: z.string(),
  title: z.string().nullable(),
  truncated: z.boolean(),
  url: webHttpUrlSchema,
});

export const updateConversationWebSearchSchema = z.object({
  webSearchEnabled: z.boolean(),
});

export type WebFetchModeDTO = z.infer<typeof webFetchModeSchema>;
export type WebFetchRequestDTO = z.infer<typeof webFetchRequestSchema>;
export type WebFetchResponseDTO = z.infer<typeof webFetchResponseSchema>;
export type ChatRouteActionDTO = z.infer<typeof chatRouteActionSchema>;
export type ChatRouteDecisionDTO = z.infer<typeof chatRouteDecisionSchema>;
export type WebResearchActivityDTO = z.infer<typeof webResearchActivitySchema>;
export type WebResearchSynthesisStageDTO = z.infer<typeof webResearchSynthesisStageSchema>;
export type WebResearchFreshnessDTO = z.infer<typeof webResearchFreshnessSchema>;
export type WebResearchModeDTO = z.infer<typeof webResearchModeSchema>;
export type WebResearchPlanDTO = z.infer<typeof webResearchPlanSchema>;
export type WebResearchReasonDTO = z.infer<typeof webResearchReasonSchema>;
export type WebSearchRequestDTO = z.infer<typeof webSearchRequestSchema>;
export type WebSearchFallbackReasonDTO = z.infer<typeof webSearchFallbackReasonSchema>;
export type WebSearchProviderDTO = z.infer<typeof webSearchProviderSchema>;
export type LegacyWebSearchProviderDTO = z.infer<typeof legacyWebSearchProviderSchema>;
export type WebSearchUnavailableReasonDTO = z.infer<typeof webSearchUnavailableReasonSchema>;
export type WebToolsErrorResponseDTO = z.infer<typeof webToolsErrorResponseSchema>;
export type WebSearchTimeRangeDTO = z.infer<typeof webSearchTimeRangeSchema>;
export type WebSearchResponseDTO = z.infer<typeof webSearchResponseSchema>;
export type WebSearchResultDTO = z.infer<typeof webSearchResultSchema>;
export type WebSourceKindDTO = z.infer<typeof webSourceKindSchema>;
export type UpdateConversationWebSearchDTO = z.infer<typeof updateConversationWebSearchSchema>;
